import { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Stack,
  TextField,
  Button,
  Snackbar,
  Alert,
  Slider,
  Tooltip,
  IconButton,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useAuth } from '../hooks/useAuth';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

interface ModeInfo {
  displayName: string;
  pricing: {
    input: number;
    output: number;
  };
  description: string;
}

interface ModelInfo {
  standard: {
    questionExtraction: ModeInfo;
    answerGeneration: ModeInfo;
    cohesionReview: ModeInfo;
  };
  budget: {
    questionExtraction: ModeInfo;
    answerGeneration: ModeInfo;
    cohesionReview: ModeInfo;
  };
  test: {
    questionExtraction: ModeInfo;
    answerGeneration: ModeInfo;
    cohesionReview: ModeInfo;
  };
  currentMode: 'standard' | 'budget' | 'test';
  userPreference: 'standard' | 'budget' | 'test' | null;
}

const MAX_PROMPT_LENGTH = 500;
const MIN_TIMEOUT_MINUTES = 1;
const MAX_TIMEOUT_MINUTES = 60;
const DEFAULT_TIMEOUT_MINUTES = 15;

export default function Settings() {
  const { user, updateSystemPrompt, updateMaxTimeout, updateModelMode } = useAuth();
  const [prompt, setPrompt] = useState(user?.systemPrompt || '');
  const [timeoutMinutes, setTimeoutMinutes] = useState(
    user?.maxClaudeCodeTimeout ? Math.round(user.maxClaudeCodeTimeout / 60000) : DEFAULT_TIMEOUT_MINUTES
  );
  const [modelMode, setModelMode] = useState<'standard' | 'budget' | 'test' | null>(user?.modelMode ?? null);
  const [modelInfo, setModelInfo] = useState<ModelInfo | null>(null);
  const [loadingModelInfo, setLoadingModelInfo] = useState(true);
  const [showSuccess, setShowSuccess] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [enablePlanningMode, setEnablePlanningMode] = useState(false);

  // Fetch model info and preferences on mount
  useEffect(() => {
    const fetchModelInfo = async () => {
      try {
        setLoadingModelInfo(true);
        const response = await axios.get(`${API_URL}/api/users/model-info`, {
          withCredentials: true
        });
        setModelInfo(response.data);
      } catch (err) {
        console.error('Failed to fetch model info:', err);
        setError('Failed to load model information');
      } finally {
        setLoadingModelInfo(false);
      }
    };

    const fetchPreferences = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/users/preferences`, {
          withCredentials: true
        });
        setEnablePlanningMode(response.data.preferences?.enablePlanningMode || false);
      } catch (err) {
        console.error('Failed to fetch preferences:', err);
      }
    };

    if (user) {
      fetchModelInfo();
      fetchPreferences();
    }
  }, [user]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await updateSystemPrompt(prompt);
      await updateMaxTimeout(timeoutMinutes * 60000); // Convert minutes to milliseconds
      await updateModelMode(modelMode);

      // Update planning mode preference
      await axios.patch(
        `${API_URL}/api/users/preferences`,
        { enablePlanningMode },
        { withCredentials: true }
      );

      setShowSuccess(true);
    } catch {
      setError('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCloseSnackbar = () => {
    setShowSuccess(false);
  };

  const isOverLimit = prompt.length > MAX_PROMPT_LENGTH;

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box mb={4}>
        <Stack direction="row" spacing={2} alignItems="center" mb={2}>
          <SettingsIcon fontSize="large" color="primary" />
          <Typography variant="h4" component="h1" fontWeight={600}>
            Settings
          </Typography>
        </Stack>
        <Typography variant="body1" color="text.secondary">
          Configure application preferences and settings
        </Typography>
      </Box>

      <Paper elevation={1} sx={{ overflow: 'hidden' }}>
        {/* Custom System Prompt Section */}
        <Box sx={{ p: 3, pb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Custom System Prompt
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Add custom instructions to tailor your proposal generation experience. These instructions will be added to the top of your claude.md file.
            </Typography>

            <TextField
              fullWidth
              multiline
              rows={4}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="e.g., Keep sentences short and concise. Don't use scientific language."
              error={isOverLimit}
              helperText={
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>
                    {isOverLimit ? 'Character limit exceeded' : 'Custom instructions for proposal generation'}
                  </span>
                  <span style={{ color: isOverLimit ? '#d32f2f' : 'inherit' }}>
                    {prompt.length}/{MAX_PROMPT_LENGTH}
                  </span>
                </Box>
              }
            />
          </Box>

          <Divider />

          {/* Model Mode Section */}
          <Box sx={{ p: 3, pb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6">
                Model Mode
              </Typography>
              <Tooltip
                title="Choose the model configuration for your proposals. Standard uses the highest quality models, Budget balances quality and cost, and Test uses fast models for development."
                arrow
                placement="top"
              >
                <IconButton size="small" sx={{ ml: 1 }}>
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            <Typography variant="body2" color="text.secondary" mb={3}>
              {modelInfo
                ? `Currently using ${modelInfo.currentMode.charAt(0).toUpperCase() + modelInfo.currentMode.slice(1)} Mode (${modelInfo.userPreference === null ? 'environment default' : 'user preference'})`
                : 'Loading model configuration...'}
            </Typography>

            {loadingModelInfo ? (
              <Typography variant="body2" color="text.secondary">
                Loading model configuration...
              </Typography>
            ) : modelInfo ? (
              <>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel id="model-mode-label">Model Mode</InputLabel>
                  <Select
                    labelId="model-mode-label"
                    value={modelMode || 'standard'}
                    label="Model Mode"
                    onChange={(e) => setModelMode(e.target.value as 'standard' | 'budget' | 'test')}
                  >
                    <MenuItem value="standard">
                      <Box>
                        <Typography variant="body1" fontWeight={600}>
                          Standard (Recommended)
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Highest quality - {modelInfo.standard.answerGeneration.displayName}
                        </Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value="budget">
                      <Box>
                        <Typography variant="body1" fontWeight={600}>
                          Budget
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Balanced quality and cost - Sonnet for answers
                        </Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value="test">
                      <Box>
                        <Typography variant="body1" fontWeight={600}>
                          Test (Development Only)
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Fast and cheap - {modelInfo.test.answerGeneration.displayName}
                        </Typography>
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>

                <Alert severity={modelMode === 'standard' ? 'info' : modelMode === 'budget' ? 'success' : 'warning'} sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    {modelMode === 'standard' ? (
                      <>
                        <strong>Standard Mode:</strong> Uses {modelInfo.standard.answerGeneration.displayName} (${modelInfo.standard.answerGeneration.pricing.input}/${modelInfo.standard.answerGeneration.pricing.output} per million tokens) for all tasks. {modelInfo.standard.answerGeneration.description}
                      </>
                    ) : modelMode === 'budget' ? (
                      <>
                        <strong>Budget Mode:</strong> Uses {modelInfo.budget.answerGeneration.displayName} for answer generation (${modelInfo.budget.answerGeneration.pricing.input}/${modelInfo.budget.answerGeneration.pricing.output} per million tokens) and {modelInfo.budget.questionExtraction.displayName} for simpler tasks. Good balance of quality and cost.
                      </>
                    ) : (
                      <>
                        <strong>Test Mode:</strong> Uses {modelInfo.test.answerGeneration.displayName} (${modelInfo.test.answerGeneration.pricing.input}/${modelInfo.test.answerGeneration.pricing.output} per million tokens) for all tasks. Fast and cheap, but may miss context. Use only for development and testing.
                      </>
                    )}
                  </Typography>
                </Alert>
              </>
            ) : (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  Unable to load model configuration. Please refresh the page.
                </Typography>
              </Alert>
            )}
          </Box>

          <Divider />

          {/* Maximum Timeout Section */}
          <Box sx={{ p: 3, pb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6">
                Maximum Timeout
              </Typography>
              <Tooltip
                title="This setting controls the maximum time allowed for proposal generation before timeout. Longer timeouts can handle larger proposals with more context, but operations won't automatically cost more just because you increase the timeout."
                arrow
                placement="top"
              >
                <IconButton size="small" sx={{ ml: 1 }}>
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            <Typography variant="body2" color="text.secondary" mb={3}>
              Set the maximum time allowed for proposal generation. For larger proposals with extensive context, increase this value to prevent timeouts.
            </Typography>

            <Box sx={{ px: 2, mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Current setting: <strong>{timeoutMinutes} minute{timeoutMinutes !== 1 ? 's' : ''}</strong>
              </Typography>
              <Slider
                value={timeoutMinutes}
                onChange={(_, value) => setTimeoutMinutes(value as number)}
                min={MIN_TIMEOUT_MINUTES}
                max={MAX_TIMEOUT_MINUTES}
                step={1}
                marks={[
                  { value: 1, label: '1 min' },
                  { value: 15, label: '15 min' },
                  { value: 30, label: '30 min' },
                  { value: 60, label: '60 min' }
                ]}
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => `${value} min`}
              />
            </Box>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Recommended:</strong> 15-30 minutes for most proposals. Increase for very large proposals with extensive context documents.
              </Typography>
            </Alert>
          </Box>

          <Divider />

          {/* Planning Mode Section */}
          <Box sx={{ p: 3, pb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6">
                Pre-Generation Planning
              </Typography>
              <Tooltip
                title="When enabled, Claude will first generate a short plan before creating the full proposal. You can review and approve the plan before proceeding with full generation."
                arrow
                placement="top"
              >
                <IconButton size="small" sx={{ ml: 1 }}>
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            <Typography variant="body2" color="text.secondary" mb={2}>
              Enable a two-phase generation workflow that shows you a plan before creating the full proposal.
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={enablePlanningMode}
                  onChange={(e) => setEnablePlanningMode(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body1">
                    Enable Pre-Generation Planning
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Show a short plan before full proposal generation. Review and approve
                    the plan to prevent expensive generation if the approach is off-target.
                    Adds ~30 seconds and ~$0.03 per RFP.
                  </Typography>
                </Box>
              }
            />

            <Alert severity={enablePlanningMode ? "success" : "info"} sx={{ mt: 2 }}>
              <Typography variant="body2">
                {enablePlanningMode ? (
                  <>
                    <strong>Planning Mode Enabled:</strong> Claude will create a proposal plan for your review before generating the full proposal. You'll see the extracted questions, approach for each, and overall strategy. This adds a small cost (~$0.03) but can save significant costs by catching poor approaches early.
                  </>
                ) : (
                  <>
                    <strong>Planning Mode Disabled:</strong> Proposals will be generated immediately without a planning phase. This is faster but doesn't provide early feedback on the generation approach.
                  </>
                )}
              </Typography>
            </Alert>
          </Box>

          <Divider />

        {/* Save Button Section */}
        <Box sx={{ p: 3, pt: 2, bgcolor: 'grey.50', display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isOverLimit || saving}
            size="medium"
            sx={{ minWidth: 140 }}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </Box>
      </Paper>

      <Snackbar
        open={showSuccess}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          Settings saved successfully!
        </Alert>
      </Snackbar>

      {error && (
        <Snackbar
          open={!!error}
          autoHideDuration={5000}
          onClose={() => setError(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
            {error}
          </Alert>
        </Snackbar>
      )}
    </Container>
  );
}
